<template>
  <div :class="['tm-flag', sizeClass, flagTypeClass]">
    <img
      class="tm-flag__img"
      :src="placeholderImageBase64"
      :alt="countryName"
    />
  </div>
</template>

<script lang="ts">
import { kebabCase } from 'lodash-es'
import { computed, defineComponent, type PropType } from '@/composition/vue/compositionApi'
import type { FlagSize, SizeProp } from '@/components/shared/types'
import { FlagType, getFlagProps } from '@/composition/flag'
import type { TmCountryCode, TmUndefinedCountryCode, TmEmptyCountryCode } from '@/services/types'
import { getCountryService } from '@/core/container/ioc'
import type { StylesheetSizeProp, UrlWrappedAssetPath } from '@/types'
import TmAbsurdError from '@/core/error/tmAbsurdError'
import { getImageSetFromMetadata } from '@/utils/imageSet'
import flagOtherImageUrl from '@/assets/images/flags/flag_other.svg'
import flagsGeneralImageMetadata from '@/assets/images/flags/flags_general.png?w=22;44&basePixels=22&format=png;avif;webp&as=metadata'
import flagsExtendedImageMetadataImageMetadata from '@/assets/images/flags/flags_extended.png?w=22;44&basePixels=22&format=png;avif;webp&as=metadata'

const placeholderImageBase64 =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACwAAAAeAQMAAABt+G7kAAAAA1BMVEVMaXFNx9g6AAAAAXRSTlMAQObYZgAAAAtJREFUeF5jGHYAAADSAAEzF5gpAAAAAElFTkSuQmCC'

export default defineComponent({
  props: {
    countryCode: {
      type: String as PropType<TmCountryCode | typeof TmUndefinedCountryCode | typeof TmEmptyCountryCode>,
    },
    size: {
      type: String as SizeProp<FlagSize>,
      default: '',
    },
  },
  setup(props) {
    const countryService = getCountryService()

    const countryName = computed(() => (props.countryCode ? countryService.getCountryName(props.countryCode) : ''))
    const sizeClass = computed(() => (props.size ? `tm-flag--${kebabCase(props.size)}` : ''))

    const flagProps = computed(() => getFlagProps(props.countryCode ?? ''))
    const flagType = computed(() => flagProps.value.type)
    const flagTypeClass = computed(() => {
      switch (flagProps.value.type) {
        case FlagType.General:
          return 'tm-flag--general'
        case FlagType.Extended:
          return 'tm-flag--alt'
        case FlagType.Other:
          return 'tm-flag--other'
        default:
          throw new TmAbsurdError(flagProps.value.type)
      }
    })
    const flagPosition = computed<StylesheetSizeProp>(() => `${flagProps.value.position * 100}%`)

    return {
      placeholderImageBase64,
      countryName,
      flagType,
      flagTypeClass,
      flagPosition,
      sizeClass,
      flagOtherImageUrl: `url(${flagOtherImageUrl})` satisfies UrlWrappedAssetPath,
      generalFlagImages: getImageSetFromMetadata(flagsGeneralImageMetadata),
      altFlagImages: getImageSetFromMetadata(flagsExtendedImageMetadataImageMetadata),
    }
  },
})
</script>

<style lang="scss" scoped>
.tm-flag {
  flex: 0 auto;
  display: flex;
  align-items: center;
  width: 22px;
  height: 22px;

  &__img {
    width: 22px;
    height: 15px;
    border-radius: $border-radius-sm;
    box-shadow: 0 0 1px rgba($black, 0.25);
    background-size: 100%;
    background-position-y: v-bind(flagPosition);
    opacity: var(--tm-flag-opacity, 0.8);
  }

  &--xx-small {
    width: 12px;
    height: 12px;
  }
  &--xx-small &__img {
    width: 12px;
    height: 8px;
    border-radius: 1px;
  }

  &--x-small {
    width: 16px;
    height: 16px;
  }
  &--x-small &__img {
    width: 16px;
    height: 11px;
    border-radius: 1px;
  }

  &--small {
    width: 19px;
    height: 19px;
  }
  &--small &__img {
    width: 19px;
    height: 13px;
  }

  &--large {
    width: 40px;
    height: 40px;
  }
  &--large &__img {
    width: 40px;
    height: 27px;
    box-shadow: none;
  }
}

.tm-flag--other {
  .tm-flag__img {
    background-image: v-bind(flagOtherImageUrl);
  }
}

.tm-flag--general {
  .tm-flag__img {
    background-image: v-bind('generalFlagImages.fallback');

    @supports (background-image: image-set(url('img.avif') type('image/avif') 2x)) {
      background-image: v-bind('generalFlagImages.imageSet');
    }
  }
}

.tm-flag--alt {
  .tm-flag__img {
    background-image: v-bind('altFlagImages.fallback');

    @supports (background-image: image-set(url('img.avif') type('image/avif') 2x)) {
      background-image: v-bind('altFlagImages.imageSet');
    }
  }
}
</style>
